import type { FeedLog } from '../types/production'

import { normalizeToIsoZMidnight } from './dateNormalization'
import { resolveCanonicalLotKey } from './lotIdentity'
import { getZonedIsoDate, resolveTimeZone } from './timeBucketing'

export type FeedDailyTotal = {
  dayKey: string // YYYY-MM-DD
  lotKey: string
  totalFeedKg: number
  eventCount: number
  lastRecordedAt?: string
}

export type FeedKgDerivationMeta = {
  source: 'derived' | 'manual' | 'empty'
  derivedKg?: number
  derivedUpdatedAt?: string
  derivedEventCount?: number
  derivedLastRecordedAt?: string
  manualKg?: number
  manualUpdatedAt?: string
}

const resolveFeedLotToken = (value: string | null | undefined): string => {
  const token = resolveCanonicalLotKey(value)
  return token === 'unknown-lot' ? '' : token
}

const parseNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const sanitized = trimmed.replace(/,/g, '')
    const numeric = Number(sanitized)
    return Number.isFinite(numeric) ? numeric : null
  }
  return null
}

// v2.1 #47: when a `timeZone` is provided, derive the day key in that zone
// (matching the user's local calendar) instead of using the UTC date prefix
// of the stored ISO-Z instant. Without `timeZone`, the legacy UTC-prefix
// behavior is preserved for backward compatibility (tests + call sites that
// have not been TZ-plumbed yet). The TZ-aware branch is what callers need
// when stored `date` strings are ISO-Z instants that crossed the UTC
// midnight boundary at parse time — see #47 chronology and the dedup
// detector callers in productionSummaryImport, waterImport, and the
// legacy duplicate detector consumed by Datatable's "Fuse duplicates"
// affordance.
export const toDayKeyDateOnly = (
  raw: string | null | undefined,
  options?: { timeZone?: string }
): string | null => {
  if (!raw) return null
  if (options?.timeZone) {
    return getZonedIsoDate(raw, resolveTimeZone(options.timeZone))
  }
  const normalized = normalizeToIsoZMidnight(raw)
  if (!normalized.ok) return null
  return normalized.isoDate
}

export const toDayKeyForInstantInTimeZone = (
  raw: string | null | undefined,
  timeZone?: string
): string | null => {
  if (!raw) return null
  const resolved = resolveTimeZone(timeZone)
  return getZonedIsoDate(raw, resolved)
}

export const makeDayLotKey = (dayKey: string, lotKey: string): string => `${dayKey}::${lotKey}`

/**
 * Findings #136: the production rows' own label for each lot id, so a feed
 * event is grouped under the label of the row it belongs to — by lot id
 * first. Crew Mode feedings may carry only `lotId`.
 */
export const buildLotLabelByIdFromRows = (
  rows: ReadonlyArray<{ lot?: string | null; lotId?: string | null }>
): Map<string, string> => {
  const labels = new Map<string, string>()
  rows.forEach((row) => {
    const lotId = (row.lotId ?? '').trim()
    const label = (row.lot ?? '').trim()
    if (lotId && label && !labels.has(lotId)) labels.set(lotId, label)
  })
  return labels
}

export const buildFeedDailyTotalsFromEvents = (
  events: FeedLog[],
  options?: { timeZone?: string; lotLabelById?: ReadonlyMap<string, string> }
): Map<string, FeedDailyTotal> => {
  const totals = new Map<string, FeedDailyTotal>()
  const resolvedTimeZone = resolveTimeZone(options?.timeZone)

  events.forEach((event) => {
    const labelFromId = event.lotId ? options?.lotLabelById?.get(event.lotId.trim()) : undefined
    const lotKey = resolveFeedLotToken(labelFromId ?? event.lot)
    if (!lotKey) return

    const dayKey = toDayKeyForInstantInTimeZone(event.recordedAt, resolvedTimeZone)
    if (!dayKey) return

    const kg = parseNumber(event.feedKg) ?? 0
    const key = makeDayLotKey(dayKey, lotKey)

    const existing = totals.get(key)
    if (!existing) {
      totals.set(key, {
        dayKey,
        lotKey,
        totalFeedKg: kg,
        eventCount: 1,
        lastRecordedAt: event.recordedAt
      })
      return
    }

    existing.totalFeedKg += kg
    existing.eventCount += 1

    const prevTime = existing.lastRecordedAt ? new Date(existing.lastRecordedAt).getTime() : Number.NEGATIVE_INFINITY
    const nextTime = new Date(event.recordedAt).getTime()
    if (Number.isFinite(nextTime) && nextTime > prevTime) {
      existing.lastRecordedAt = event.recordedAt
    }
  })

  return totals
}

export type ProductionFeedRow = {
  id: string
  date?: string
  lot?: string
  feedKg?: string
}

export type ApplyFeedKgDerivationResult<Row extends ProductionFeedRow> = {
  rows: Row[]
  metaByRowId: Record<string, FeedKgDerivationMeta>
  rowsChanged: boolean
  metaChanged: boolean
}

export const applyFeedKgDerivationToProductionRows = <Row extends ProductionFeedRow>(input: {
  rows: Row[]
  feedTotals: Map<string, FeedDailyTotal>
  metaByRowId: Record<string, FeedKgDerivationMeta>
  nowIso: string
  /**
   * v2.1 #47: optional farm/browser timezone used to derive the `(lot, day)`
   * grouping key from each row's stored ISO-Z `date`. When omitted, the
   * legacy UTC-prefix behavior is preserved (back-compat for tests). Callers
   * with farm context should pass `resolveTimeZone(getTimeZoneFromFarm(farm))`
   * so that late-evening rows that crossed the UTC boundary at parse time
   * group with their local calendar day (the day the operator typed in the
   * CSV), matching what feed events themselves group by.
   */
  timeZone?: string
}): ApplyFeedKgDerivationResult<Row> => {
  const { rows, feedTotals, metaByRowId, nowIso, timeZone } = input
  const dayKeyOptions = timeZone ? { timeZone } : undefined

  const groupCounts = new Map<string, number>()
  rows.forEach((row) => {
    const lotKey = resolveFeedLotToken(row.lot)
    const dayKey = toDayKeyDateOnly(row.date, dayKeyOptions)
    if (!lotKey || !dayKey) return
    const key = makeDayLotKey(dayKey, lotKey)
    groupCounts.set(key, (groupCounts.get(key) ?? 0) + 1)
  })

  let rowsChanged = false
  let metaChanged = false
  const nextRows = rows.map((row) => {
    const lotKey = resolveFeedLotToken(row.lot)
    const dayKey = toDayKeyDateOnly(row.date, dayKeyOptions)
    if (!lotKey || !dayKey) return row

    const dayLotKey = makeDayLotKey(dayKey, lotKey)
    if ((groupCounts.get(dayLotKey) ?? 0) !== 1) return row

    const derived = feedTotals.get(dayLotKey)
    if (!derived || derived.eventCount <= 0) return row

    const currentFeedKg = (row.feedKg ?? '').toString().trim()
    const currentMeta = metaByRowId[row.id]
    const shouldStoreMeta = Boolean(currentMeta) || currentFeedKg === ''

    const nextMeta: FeedKgDerivationMeta = {
      ...(currentMeta ?? { source: 'empty' }),
      derivedKg: derived.totalFeedKg,
      derivedUpdatedAt: nowIso,
      derivedEventCount: derived.eventCount,
      derivedLastRecordedAt: derived.lastRecordedAt
    }

    if (shouldStoreMeta) {
      if (!currentMeta || JSON.stringify(currentMeta) !== JSON.stringify(nextMeta)) {
        metaByRowId[row.id] = nextMeta
        metaChanged = true
      }
    }

    if (nextMeta.source === 'manual') {
      return row
    }

    // If we are not tracking this row and it already has a value, do not change it.
    if (!shouldStoreMeta && currentFeedKg !== '') {
      return row
    }
    const canAutofillEmpty = currentFeedKg === ''
    const canUpdateDerived = nextMeta.source === 'derived'

    if (!canAutofillEmpty && !canUpdateDerived) {
      return row
    }

    const nextFeedKg = derived.totalFeedKg.toString()
    if (currentFeedKg === nextFeedKg) {
      if (nextMeta.source !== 'derived') {
        metaByRowId[row.id] = { ...nextMeta, source: 'derived' }
        metaChanged = true
      }
      return row
    }

    rowsChanged = true
    metaByRowId[row.id] = { ...nextMeta, source: 'derived' }
    return { ...row, feedKg: nextFeedKg }
  })

  return { rows: nextRows, metaByRowId, rowsChanged, metaChanged }
}

export const updateFeedKgMetaAfterUserEdit = (
  metaByRowId: Record<string, FeedKgDerivationMeta>,
  rowId: string,
  nextFeedKgValue: string,
  nowIso: string
): Record<string, FeedKgDerivationMeta> => {
  const trimmed = (nextFeedKgValue ?? '').toString().trim()
  const existing = metaByRowId[rowId]

  if (!trimmed) {
    const next: FeedKgDerivationMeta = {
      ...(existing ?? { source: 'empty' }),
      source: 'empty'
    }
    delete next.manualKg
    delete next.manualUpdatedAt
    return { ...metaByRowId, [rowId]: next }
  }

  const numeric = parseNumber(trimmed)
  const next: FeedKgDerivationMeta = {
    ...(existing ?? { source: 'manual' }),
    source: 'manual',
    manualKg: numeric ?? undefined,
    manualUpdatedAt: nowIso
  }

  return { ...metaByRowId, [rowId]: next }
}

/**
 * Findings #207: an auto-filled feed kg that came from a deleted lot's
 * feedings (same day and lot name) is cleared when no feeding of an active lot
 * backs that day: the value matches the deleted lot's total exactly and the
 * row's kg is marked derived. Manual values are never touched.
 */
export const clearFeedKgDerivedFromDeletedLots = <Row extends ProductionFeedRow>(input: {
  rows: Row[]
  currentTotals: Map<string, FeedDailyTotal>
  deletedTotals: Map<string, FeedDailyTotal>
  metaByRowId: Record<string, FeedKgDerivationMeta>
  timeZone?: string
}): ApplyFeedKgDerivationResult<Row> => {
  const { rows, currentTotals, deletedTotals, metaByRowId, timeZone } = input
  const dayKeyOptions = timeZone ? { timeZone } : undefined
  let changed = false
  const nextRows = rows.map((row) => {
    const lotKey = resolveFeedLotToken(row.lot)
    const dayKey = toDayKeyDateOnly(row.date, dayKeyOptions)
    if (!lotKey || !dayKey) return row
    const key = makeDayLotKey(dayKey, lotKey)
    if (currentTotals.has(key)) return row
    const fromDeleted = deletedTotals.get(key)
    if (!fromDeleted || metaByRowId[row.id]?.source !== 'derived') return row
    if (parseNumber(row.feedKg) !== fromDeleted.totalFeedKg) return row
    changed = true
    metaByRowId[row.id] = { source: 'empty' }
    return { ...row, feedKg: '' }
  })
  return { rows: nextRows, metaByRowId, rowsChanged: changed, metaChanged: changed }
}
